import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type Portal = 'client' | 'practitioner' | 'admin' | 'host' | 'unknown';

const PREFERRED_PORTAL_KEY = 'resonow_preferred_portal';

export const usePortalResolver = () => {
  const { user } = useAuth();
  const [preferredPortal, setPreferredPortal] = useState<Portal | null>(null);

  const resolvePortal = useCallback(async () => {
    if (!user) return 'unknown' as Portal;
    try {
      const cached = sessionStorage.getItem(PREFERRED_PORTAL_KEY) as Portal | null;
      if (cached && cached !== 'unknown') {
        setPreferredPortal(cached);
        return cached;
      }
      const { data, error } = await supabase.rpc('get_user_portal_status', { p_user_id: user.id });
      if (error) {
        console.error('get_user_portal_status error', error);
        return 'unknown' as Portal;
      }
      const portal = ((data as any)?.preferred_portal as Portal) ?? 'unknown';
      sessionStorage.setItem(PREFERRED_PORTAL_KEY, portal);
      setPreferredPortal(portal);
      return portal;
    } catch (e) {
      console.error('resolvePortal failed', e);
      return 'unknown' as Portal;
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setPreferredPortal(null);
      sessionStorage.removeItem(PREFERRED_PORTAL_KEY);
      return;
    }
    void resolvePortal();
  }, [user, resolvePortal]);

  return { preferredPortal, resolvePortal };
};
